import { Spacing } from '@persona-id/design-tokens';
import { PlusIcon } from '@persona-id/design-tokens/icons';
import { useCallback, useMemo, useRef, useState, VFC } from 'react';
import styled from 'styled-components';

import FormControl from 'dashboard/components/HandlebarFormControl/FormControl';
import JsonSchemaSelect, { Option } from 'dashboard/components/JsonSchemaSelect';
import { callHelper, formatHandlebarValue } from 'dashboard/lib/utils/handlebars';
import { valueTransformer } from 'dashboard/lib/workflows';
import { pathIsNormative } from 'dashboard/lib/workflows/method-chain';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import { InputProps } from 'design-system/components/Input';
import { TextAreaProps } from 'design-system/components/TextArea';
import { Elevation } from 'design-system/variables/effect';
import { JsonSchemaCustomTypes } from 'lib/fields';
import useToggle from 'lib/hooks/useToggle';
import { JsonSchema, JsonSchemaBasicTypes, JsonSchemaTypes } from 'lib/json-schema';

const HandlebarWrapper = styled.div`
  position: relative;
`;

const FloatingButton = styled(Button)`
  position: absolute;
  top: 1px;
  right: 1px;
  height: calc(100% - 2px);
  width: 24px;
  pointer-events: all;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
`;

const FloatingSelectBox = styled.div`
  position: absolute;
  width: 100%;
  top: 100%;
  margin-top: ${Spacing[1]};
  pointer-events: all;
  z-index: ${Z_INDICES.dropdown};
  box-shadow: ${Elevation.Mid};
`;

type FormControlType = InputProps & TextAreaProps;

interface HandlebarFormControlProps extends FormControlType {
  type: 'input' | 'text-area' | 'code';
  value: string | undefined;
  editable: boolean;
  pillsEditable?: boolean;
  jsonSchema: JsonSchema;
  tokensToDisplayValue: Record<string, string>;
  allowedFieldPaths?: string[];
  updateValue: (value: string) => void;
  allowedTypes?: (JsonSchemaTypes | JsonSchemaCustomTypes)[];
  allowedArrayTypes?: (JsonSchemaBasicTypes | JsonSchemaTypes.Object)[];
  addPillButtonDisabled?: boolean;
  canSelectObjects?: boolean;
  fieldPathsAreStrictMatch?: boolean;
  disallowedFieldPaths?: string[];
}

/**
 * Input/Textarea wrapper to support JsonSchemaSelect with given schema.
 *
 * @param {'input' | 'text-area' | 'code'} type - Rendering whether input, text area, or code editor
 * @param {string} value - field's value
 * @param {boolean} editable - field's editability
 * @param {JsonSchema} jsonSchema - JSON Schema for arbitrary fields
 * @param {(value: string) => void} updateValue - A handler to update inputted value
 */
const HandlebarFormControl: VFC<HandlebarFormControlProps> = ({
  type,
  value: initialValue = '',
  editable,
  jsonSchema,
  tokensToDisplayValue,
  allowedFieldPaths = [],
  allowedArrayTypes,
  updateValue,
  allowedTypes = [],
  pillsEditable,
  addPillButtonDisabled,
  canSelectObjects,
  fieldPathsAreStrictMatch,
  disallowedFieldPaths,
  ...otherProps
}) => {
  const { getDisplayValue, getTokenizedValue } = useMemo(
    () => valueTransformer(tokensToDisplayValue),
    [tokensToDisplayValue],
  );
  const value = useMemo(
    () => formatHandlebarValue(initialValue, getDisplayValue),
    [initialValue, getDisplayValue],
  );
  const textRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const [lastPositionOnCode, setLastPositionOnCode] = useState(0);

  const wrappedUpdateValue = useCallback(
    (val: string) => {
      updateValue(formatHandlebarValue(val, getTokenizedValue));
    },
    [updateValue, getTokenizedValue],
  );
  const handleSelect = (handlebar: string) => {
    if (!pathIsNormative(handlebar)) {
      handlebar = callHelper(handlebar);
    }
    const cursorIndex = type === 'code' ? lastPositionOnCode : textRef.current?.selectionStart;
    wrappedUpdateValue(`${value.slice(0, cursorIndex)}{{${handlebar}}}${value.slice(cursorIndex)}`);
  };

  const [isDiggingSchema, { enable: startDigging, disable: endDigging }] = useToggle(false);
  const [inputValue, setInputValue] = useState<string | string[]>('');

  const renderHandlebarSelect = () => {
    if (!isDiggingSchema) {
      return (
        <FloatingButton
          id='handlebar-form-control__button-for-trigger'
          data-test='handlebar-form-control__button-for-trigger'
          aria-label='add handlebars'
          onClick={startDigging}
          size='sm'
          variant='basic'
          width='icon'
          disabled={addPillButtonDisabled}
        >
          <PlusIcon />
        </FloatingButton>
      );
    }
    return (
      <FloatingSelectBox>
        <JsonSchemaSelect
          id={otherProps.id}
          inputValue={inputValue}
          jsonSchema={jsonSchema}
          autoFocus
          allowedArrayTypes={allowedArrayTypes}
          allowedFieldPaths={allowedFieldPaths}
          allowedTypes={allowedTypes}
          tokensToDisplayValue={tokensToDisplayValue}
          fieldPathsAreStrictMatch={fieldPathsAreStrictMatch}
          disallowedFieldPaths={disallowedFieldPaths}
          onValueChange={setInputValue}
          canSelectObjects={canSelectObjects}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          onFieldSelect={(field: Option) => {
            handleSelect(field.value);
            setInputValue('');
            endDigging();
          }}
          onBlur={endDigging}
        />
      </FloatingSelectBox>
    );
  };

  return (
    <HandlebarWrapper>
      <FormControl
        allowedFieldPaths={allowedFieldPaths}
        editable={editable}
        focused={isDiggingSchema}
        jsonSchema={jsonSchema}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        textRef={textRef}
        pillsEditable={pillsEditable ?? editable}
        type={type}
        value={value}
        onUpdateCursorOnCode={setLastPositionOnCode}
        onValueChange={wrappedUpdateValue}
        {...otherProps}
      />
      {renderHandlebarSelect()}
    </HandlebarWrapper>
  );
};

export default HandlebarFormControl;
